import { Box, Chip, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import type { ColumnConfig } from '../../Shared/Components/DataTable';
import {
  formatBookingPaymentStatusLabel,
  getBookingPaymentStatusAmount,
  getBookingPaymentStatusColor,
  normalizeBookingPaymentStatus,
} from '../Helpers/bookingPaymentStatusHelpers';
import { formatBookingCurrency } from '../Helpers/formatBookingCurrency';
import { getBookingReservationApplicantDisplay } from '../Helpers/getBookingReservationApplicantDisplay';
import type {
  BookingReservationPartyLookup,
  BookingReservationRow,
} from '../Types/bookingReservationTable.types';
import BookingReservationInvoiceLinksCell from './BookingReservationInvoiceLinksCell';

interface BookingReservationColumnsOptions {
  parties?: BookingReservationPartyLookup[];
}

const SUMMARY_WIDTHS = {
  createdAt: '8%',
  unitPrice: '8%',
  offerName: '14%',
  applicantDisplay: '13%',
  effectiveParticipantsCount: '5%',
  totalPrice: '7%',
  paymentStatus: '8%',
  totalPaidAmount: '8%',
  invoices: '17%',
  status: '10%',
} as const;

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  transfer: 'Przelew',
  cash: 'Gotówka',
};

const getSummaryColumnWidth = (key: keyof typeof SUMMARY_WIDTHS): string => SUMMARY_WIDTHS[key];

const renderBookingReservationStatus = (row: BookingReservationRow) => (
  <Chip
    label={row.status === 'cancelled' ? 'Anulowana' : 'Aktywna'}
    color={row.status === 'cancelled' ? 'default' : 'success'}
    size="small"
  />
);

const renderOfferName = (row: BookingReservationRow) => {
  const offerName = row.offerName || row.booking?.offerName;

  return (
    <Typography
      variant="body2"
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        lineHeight: 1.35,
      }}
    >
      {offerName || '-'}
    </Typography>
  );
};

const renderApplicantDisplay = (
  row: BookingReservationRow,
  parties: BookingReservationPartyLookup[]
) => {
  const applicant = getBookingReservationApplicantDisplay(row, parties);
  const notes = row.notes ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
      >
        {applicant.title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
      >
        {applicant.subtitle || '-'}
      </Typography>
      {notes.length > 0 && (
        <Tooltip
          title={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {notes.map((note) => (
                <Typography
                  key={note.id}
                  variant="caption"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {note.content}
                </Typography>
              ))}
            </Box>
          }
          arrow
        >
          <Typography
            variant="caption"
            color="primary"
            tabIndex={0}
            sx={{ cursor: 'default', width: 'fit-content', textDecoration: 'underline dotted' }}
          >
            Notatka {notes.length > 1 ? `(${notes.length})` : ''}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

export const createBookingReservationColumns = ({
  parties = [],
}: BookingReservationColumnsOptions = {}): ColumnConfig<BookingReservationRow>[] => [
  {
    key: 'createdAt',
    label: 'Data',
    width: getSummaryColumnWidth('createdAt'),
    render: (row) => dayjs(row.createdAt).format('DD.MM.YYYY'),
  },
  {
    key: 'unitPrice',
    label: 'Cena jedn.',
    width: getSummaryColumnWidth('unitPrice'),
    render: (row) => formatBookingCurrency(row.unitPrice),
  },
  {
    key: 'offerName',
    label: 'Nazwa oferty',
    width: getSummaryColumnWidth('offerName'),
    render: (row) => renderOfferName(row),
  },
  {
    key: 'applicantDisplay',
    label: 'Zgłaszający',
    width: getSummaryColumnWidth('applicantDisplay'),
    render: (row) => renderApplicantDisplay(row, parties),
  },
  {
    key: 'effectiveParticipantsCount',
    label: 'Ucz.',
    width: getSummaryColumnWidth('effectiveParticipantsCount'),
    render: (row) => {
      if (
        typeof row.effectiveParticipantsCount === 'number' &&
        row.effectiveParticipantsCount > 0
      ) {
        return row.effectiveParticipantsCount;
      }

      const participantsLength = row.participants?.length ?? 0;
      if (participantsLength > 0) {
        return participantsLength;
      }

      return row.declaredParticipantsCount ?? 0;
    },
  },
  {
    key: 'totalPrice',
    label: 'Cena całk.',
    width: getSummaryColumnWidth('totalPrice'),
    render: (row) => formatBookingCurrency(row.totalPrice),
  },
  {
    key: 'paymentStatus',
    label: 'Status',
    width: getSummaryColumnWidth('paymentStatus'),
    render: (row) => {
      const status = row.computedPaymentStatus || row.paymentStatus;
      const totalPaid =
        row.totalPaidAmount ?? ((row.paidAmount ?? 0) + (row.nonCashPaymentAmount ?? 0));
      const normalized = status || 'Nieopłacona';
      const amount = getBookingPaymentStatusAmount(normalized, row.totalPrice, totalPaid);
      return (
        <Chip
          label={
            amount ? (
              <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                <div>{normalizeBookingPaymentStatus(normalized)}</div>
                <div style={{ fontSize: '0.75em', opacity: 0.85 }}>{amount}</div>
              </Box>
            ) : (
              formatBookingPaymentStatusLabel(normalized, row.totalPrice, totalPaid)
            )
          }
          color={getBookingPaymentStatusColor(status)}
          size="small"
          sx={amount ? { height: 'auto', '& .MuiChip-label': { py: 0.5 } } : undefined}
        />
      );
    },
  },
  {
    key: 'totalPaidAmount',
    label: 'Wpłacono',
    width: getSummaryColumnWidth('totalPaidAmount'),
    render: (row) => {
      const totalPaid =
        row.totalPaidAmount ?? ((row.paidAmount ?? 0) + (row.nonCashPaymentAmount ?? 0));
      return formatBookingCurrency(totalPaid);
    },
  },
  {
    key: 'paymentMethod',
    label: 'Metoda',
    render: (row) => {
      const method = row.computedPaymentMethod || row.paymentMethod;
      if (!method) {
        return '-';
      }

      return PAYMENT_METHOD_LABELS[method] ?? method;
    },
  },
  {
    key: 'invoices',
    label: 'FV',
    width: getSummaryColumnWidth('invoices'),
    render: (row) => <BookingReservationInvoiceLinksCell reservationId={row.id} />,
  },
  {
    key: 'status',
    label: 'Status rezerwacji',
    width: getSummaryColumnWidth('status'),
    render: renderBookingReservationStatus,
  },
];
